import { supabase } from './supabase';
import { getTaskChecklist, assignChecklistToTask, isChecklistCompleted } from './checklists';

// Helper function to convert local datetime-local input to UTC
function convertLocalToUTC(dateString: string): string {
  // Parse the local date string components
  const [datePart, timePart] = dateString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Create date using local components
  const localDate = new Date(year, month - 1, day, hours, minutes);
  
  // Convert to UTC ISO string
  return localDate.toISOString();
}

// Helper function to convert UTC to local datetime-local format
function convertUTCToLocal(utcString: string): string {
  const date = new Date(utcString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export interface Task {
  id: string;
  business_id: string;
  resource_id?: string | null;
  title: string;
  description?: string | null;
  status: 'pending' | 'up_coming' | 'completed';
  priority: 'low' | 'medium' | 'high';
  start_date: string;
  due_date: string | null;
  is_recurring: boolean;
  recurrence_frequency: 'daily' | 'weekly' | 'monthly' | 'yearly' | null;
  recurrence_start_date: string | null;
  recurrence_end_date: string | null;
  completed_at: string | null;
  updated_at: string;
  assigned_to: string;
  created_at: string;
  constraints: {
    disableClockOut: boolean;
  } | null;
}

interface CreateTaskPayload {
  business_id: string;
  resource_id?: string | null;
  title: string;
  description?: string | null;
  priority: 'low' | 'medium' | 'high';
  start_date: string;
  due_date?: string;
  is_recurring: boolean;
  recurrence_frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrence_start_date?: string;
  recurrence_end_date?: string;
  constraints?: {
    disableClockOut: boolean;
  };
}

function calculateNextRecurrenceDate(dateString: string, frequency: string): string {
  // Parse the date string as local time
  const [datePart, timePart] = dateString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Create date using local components
  const date = new Date(year, month - 1, day, hours, minutes);
  
  switch (frequency.toLowerCase()) {
    case 'daily':
      date.setDate(date.getDate() + 1);
      break;
    case 'weekly':
      date.setDate(date.getDate() + 7);
      break;
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'yearly':
      date.setFullYear(date.getFullYear() + 1);
      break;
    default:
      throw new Error(`Invalid frequency: ${frequency}`);
  }

  // Format back to local datetime string
  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  const nextHours = String(date.getHours()).padStart(2, '0');
  const nextMinutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${nextYear}-${nextMonth}-${nextDay}T${nextHours}:${nextMinutes}`;
}

// Calculate the next occurrence dates based on the current task's dates
function calculateNextOccurrenceDates(task: Task): { nextStartDate: string, nextDueDate: string | undefined } {
  if (!task.recurrence_frequency) {
    throw new Error('Task has no recurrence frequency set');
  }

  const nextStartDate = calculateNextRecurrenceDate(task.start_date, task.recurrence_frequency);

  // If there's a due date, calculate the next due date maintaining the same time difference
  let nextDueDate: string | undefined = undefined;
  if (task.due_date) {
    // Parse dates as local time
    const [startDatePart, startTimePart] = task.start_date.split('T');
    const [startYear, startMonth, startDay] = startDatePart.split('-').map(Number);
    const [startHours, startMinutes] = startTimePart.split(':').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay, startHours, startMinutes);

    const [dueDatePart, dueTimePart] = task.due_date.split('T');
    const [dueYear, dueMonth, dueDay] = dueDatePart.split('-').map(Number);
    const [dueHours, dueMinutes] = dueTimePart.split(':').map(Number);
    const dueDate = new Date(dueYear, dueMonth - 1, dueDay, dueHours, dueMinutes);

    const timeDifference = dueDate.getTime() - startDate.getTime();
    
    // Parse next start date
    const [nextStartDatePart, nextStartTimePart] = nextStartDate.split('T');
    const [nextStartYear, nextStartMonth, nextStartDay] = nextStartDatePart.split('-').map(Number);
    const [nextStartHours, nextStartMinutes] = nextStartTimePart.split(':').map(Number);
    const nextStartDateTime = new Date(nextStartYear, nextStartMonth - 1, nextStartDay, nextStartHours, nextStartMinutes);

    // Calculate next due date
    const nextDueDateTime = new Date(nextStartDateTime.getTime() + timeDifference);
    
    // Format back to local datetime string
    const nextDueYear = nextDueDateTime.getFullYear();
    const nextDueMonth = String(nextDueDateTime.getMonth() + 1).padStart(2, '0');
    const nextDueDay = String(nextDueDateTime.getDate()).padStart(2, '0');
    const nextDueHours = String(nextDueDateTime.getHours()).padStart(2, '0');
    const nextDueMinutes = String(nextDueDateTime.getMinutes()).padStart(2, '0');
    
    nextDueDate = `${nextDueYear}-${nextDueMonth}-${nextDueDay}T${nextDueHours}:${nextDueMinutes}`;
  }

  return { nextStartDate, nextDueDate };
}

function determineInitialStatus(startDate: string): 'pending' | 'up_coming' {
  const start = new Date(startDate);
  const now = new Date();
  return start <= now ? 'pending' : 'up_coming';
}

export async function createTask(payload: CreateTaskPayload) {
  if (!supabase) throw new Error('Supabase client not initialized');

  // For recurring tasks, set the next occurrence dates if not provided
  let nextStartDate = payload.recurrence_start_date;
  let nextDueDate = payload.recurrence_end_date;

  if (payload.is_recurring && payload.recurrence_frequency && !nextStartDate) {
    const dates = calculateNextOccurrenceDates({
      ...payload,
      start_date: payload.start_date,
      due_date: payload.due_date || null,
      recurrence_frequency: payload.recurrence_frequency,
    } as Task);
    nextStartDate = dates.nextStartDate;
    nextDueDate = dates.nextDueDate;
  }

  // Convert all dates to UTC before saving to database
  const { data, error } = await supabase
    .from('tasks')
    .insert({
      business_id: payload.business_id,
      resource_id: payload.resource_id,
      title: payload.title,
      description: payload.description,
      priority: payload.priority,
      status: determineInitialStatus(payload.start_date),
      start_date: convertLocalToUTC(payload.start_date),
      due_date: payload.due_date ? convertLocalToUTC(payload.due_date) : null,
      is_recurring: payload.is_recurring,
      recurrence_frequency: payload.recurrence_frequency,
      recurrence_start_date: nextStartDate ? convertLocalToUTC(nextStartDate) : null,
      recurrence_end_date: nextDueDate ? convertLocalToUTC(nextDueDate) : null,
      constraints: payload.constraints || null
    })
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error('Failed to create task');

  // Convert dates back to local time before returning
  return {
    ...data,
    start_date: convertUTCToLocal(data.start_date),
    due_date: data.due_date ? convertUTCToLocal(data.due_date) : null,
    recurrence_start_date: data.recurrence_start_date ? convertUTCToLocal(data.recurrence_start_date) : null,
    recurrence_end_date: data.recurrence_end_date ? convertUTCToLocal(data.recurrence_end_date) : null,
  } as Task;
}

export async function getTasks(businessId: string, client = supabase): Promise<Task[]> {
  if (!client) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await client
    .from('tasks')
    .select('*')
    .eq('business_id', businessId)
    .order('start_date', { ascending: true });

  if (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }

  // Convert all dates back to local time
  return data.map(task => ({
    ...task,
    start_date: convertUTCToLocal(task.start_date),
    due_date: task.due_date ? convertUTCToLocal(task.due_date) : null,
    recurrence_start_date: task.recurrence_start_date ? convertUTCToLocal(task.recurrence_start_date) : null,
    recurrence_end_date: task.recurrence_end_date ? convertUTCToLocal(task.recurrence_end_date) : null,
  })) as Task[];
}

export async function updateTaskStatus(taskId: string, status: string, client = supabase): Promise<Task[]> {
  if (!client) {
    throw new Error('Supabase client is not initialized');
  }

  // If trying to mark as completed, check checklist completion first
  if (status === 'completed') {
    const isCompleted = await isChecklistCompleted(taskId);
    if (!isCompleted) {
      throw new Error('Cannot complete task: checklist items are not all completed');
    }
  }

  const { data: task, error: fetchError } = await client
    .from('tasks')
    .select('*')
    .eq('id', taskId)
    .single();

  if (fetchError) {
    console.error('Error fetching task:', fetchError);
    throw fetchError;
  }

  // Convert task dates to local time for processing
  const localTask = {
    ...task,
    start_date: convertUTCToLocal(task.start_date),
    due_date: task.due_date ? convertUTCToLocal(task.due_date) : null,
    recurrence_start_date: task.recurrence_start_date ? convertUTCToLocal(task.recurrence_start_date) : null,
    recurrence_end_date: task.recurrence_end_date ? convertUTCToLocal(task.recurrence_end_date) : null,
  } as Task;

  const now = new Date().toISOString();
  const updates = {
    status,
    updated_at: now,
    completed_at: status === 'completed' ? now : null
  };

  const { data: updatedTask, error: updateError } = await client
    .from('tasks')
    .update(updates)
    .eq('id', taskId)
    .select()
    .single();

  if (updateError) {
    console.error('Error updating task status:', updateError);
    throw updateError;
  }

  // If this is a recurring task being completed, create the next occurrence
  let nextTask = null;
  if (status === 'completed' && localTask.is_recurring && localTask.recurrence_frequency) {
    try {
      // Get the current task's checklist before creating the next occurrence
      const checklist = await getTaskChecklist(taskId);

      // Calculate the next occurrence dates using local times
      const { nextStartDate, nextDueDate } = calculateNextOccurrenceDates(localTask);

      // Create the next occurrence
      const nextTaskData = {
        business_id: localTask.business_id,
        resource_id: localTask.resource_id,
        title: localTask.title,
        description: localTask.description,
        priority: localTask.priority,
        start_date: nextStartDate,
        due_date: nextDueDate,
        is_recurring: true,
        recurrence_frequency: localTask.recurrence_frequency,
      };

      nextTask = await createTask(nextTaskData);

      // If there was a checklist, assign it to the new task
      if (checklist) {
        await assignChecklistToTask(nextTask.id, checklist.id);
      }
    } catch (error) {
      console.error('Error creating next occurrence of recurring task:', error);
      throw error;
    }
  }

  // Convert dates back to local time before returning
  const localUpdatedTask = {
    ...updatedTask,
    start_date: convertUTCToLocal(updatedTask.start_date),
    due_date: updatedTask.due_date ? convertUTCToLocal(updatedTask.due_date) : null,
    recurrence_start_date: updatedTask.recurrence_start_date ? convertUTCToLocal(updatedTask.recurrence_start_date) : null,
    recurrence_end_date: updatedTask.recurrence_end_date ? convertUTCToLocal(updatedTask.recurrence_end_date) : null,
  } as Task;

  return [localUpdatedTask, ...(nextTask ? [nextTask] : [])];
}

export async function hasActiveClockOutConstraint(resourceId: string): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('resource_id', resourceId)
    .eq('status', 'pending')
    .not('constraints', 'is', null);

  if (error) {
    console.error('Error checking clock out constraints:', error);
    throw error;
  }

  // Check if any of the pending tasks have disableClockOut set to true
  return data.some(task => task.constraints?.disableClockOut === true);
} 